<script>
  import { onDestroy, onMount } from "svelte";
  import Header from "@components/Header.svelte";
  import Modal from "@components/Modal.svelte";
  import { ROUTES } from "@utils/routes.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let survey;

  let surveyName = "Potential Scout";
  let surveyDetails = null;
  let schedule = null;
  let loading = true;
  let errorMessage = "";
  let busy = false;
  let feedbackMessage = "";
  let sendConfirmationOpen = false;
  let lastInvitationSendCount = 0;
  let timer;
  let now = Date.now();

  $: pending = schedule?.pending_invitations || 0;
  $: paused = schedule?.status === "paused";
  $: sent = schedule?.status === "sent";
  $: remaining = schedule?.remaining_seconds;
  $: finished = surveyDetails?.status === "FINISHED";
  $: reminderSchedules = buildReminderSchedules(surveyDetails);
  $: reminderLimit = schedule?.reminder_limit || 3;
  $: reminderRoundsSent = schedule?.reminder_rounds_sent || 0;
  $: canSendReminder =
    !finished &&
    (schedule?.eligible_reminder_recipients || 0) > 0 &&
    reminderRoundsSent < reminderLimit;

  function formatCountdown(seconds) {
    if (seconds === null || seconds === undefined) return "24:00:00";
    const value = Math.max(0, seconds);
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const secs = value % 60;
    return [hours, minutes, secs]
      .map((part) => String(part).padStart(2, "0"))
      .join(":");
  }

  function buildReminderSchedules(details) {
    if (!details?.end_date) return [];
    return [
      { label: "First reminder", days: details.reminder_days },
      { label: "Second reminder", days: details.reminder2_days },
    ]
      .filter((reminder) => reminder.days !== null && reminder.days !== undefined)
      .map((reminder) => {
        const sendAt = new Date(`${details.end_date}T09:00:00`);
        sendAt.setDate(sendAt.getDate() - Number(reminder.days));
        return { ...reminder, sendAt };
      })
      .sort((a, b) => a.sendAt - b.sendAt);
  }

  function formatReminderCountdown(sendAt) {
    const seconds = Math.max(0, Math.floor((sendAt.getTime() - now) / 1000));
    if (!seconds) return "Due";
    const days = Math.floor(seconds / 86400);
    return `${days}d ${formatCountdown(seconds % 86400)}`;
  }

  function formatReminderDate(sendAt) {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(sendAt);
  }

  async function loadSchedule() {
    try {
      const [surveyResponse, scheduleResponse] = await Promise.all([
        fetchWithSession(`/api/knextractor/surveys/${survey}`),
        fetchWithSession(`/api/knextractor/surveys/${survey}/invitation-schedule`),
      ]);
      if (!surveyResponse.ok || !scheduleResponse.ok) throw new Error();
      surveyDetails = await surveyResponse.json();
      surveyName = surveyDetails.name || surveyName;
      schedule = await scheduleResponse.json();
      if (schedule.status === "not_started" && schedule.pending_invitations > 0) {
        await changeSchedule("start");
      }
    } catch (error) {
      errorMessage = "Invitation schedule could not be loaded.";
    } finally {
      loading = false;
    }
  }

  async function changeSchedule(action) {
    busy = true;
    errorMessage = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${survey}/invitation-schedule`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        },
      );
      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error);
      schedule = data;
    } catch (error) {
      errorMessage = error.message || "Invitation schedule could not be updated.";
    } finally {
      busy = false;
    }
  }

  async function sendNow() {
    busy = true;
    errorMessage = "";
    feedbackMessage = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${survey}/send-emails`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error);
      lastInvitationSendCount = Number(data.emails_sent || 0);
      if (Number(data.pending_invitations || 0) > 0) {
        feedbackMessage = `${lastInvitationSendCount} sent; ${data.pending_invitations} remain queued for automatic retry.`;
      }
      sendConfirmationOpen = false;
      await loadSchedule();
    } catch (error) {
      errorMessage = error.message || "Invitations could not be sent.";
    } finally {
      busy = false;
    }
  }

  async function sendReminderNow() {
    busy = true;
    errorMessage = "";
    feedbackMessage = "";
    try {
      const response = await fetchWithSession(
        `/api/knextractor/surveys/${survey}/send-reminder`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error);
      feedbackMessage = data.emails_sent
        ? `${data.emails_sent} reminder${data.emails_sent === 1 ? "" : "s"} sent.`
        : "No participant currently needs another reminder.";
      await loadSchedule();
    } catch (error) {
      errorMessage = error.message || "Reminders could not be sent.";
    } finally {
      busy = false;
    }
  }

  onMount(() => {
    loadSchedule();
    timer = window.setInterval(() => {
      now = Date.now();
      if (schedule?.status === "scheduled" && schedule.remaining_seconds > 0) {
        schedule = {
          ...schedule,
          remaining_seconds: schedule.remaining_seconds - 1,
        };
      }
    }, 1000);
  });

  onDestroy(() => window.clearInterval(timer));
</script>

<Header
  projectNameOverride={surveyName}
  projectHref={ROUTES.dam.root}
  area="Potential Scout"
  areaHref={`${ROUTES.knex.potentialScope.dashboard}?survey=${survey}`}
  breadcrumb="Manage Invitations"
/>

<main class="invitation-page">
  {#if loading}
    <p>Loading invitation schedule...</p>
  {:else}
    <section class="invitation-card">
      <div class="card-header">
        <div>
          <span class:paused class:sent class="status-badge">
            {sent ? "Sent" : paused ? "Paused" : "Scheduled"}
          </span>
          <h1>Invitation delivery</h1>
          <p>
            {sent
              ? "All invitations have been sent."
              : paused
                ? "Automatic delivery is paused. Resume it whenever you are ready."
                : "Invitations will be sent automatically when the countdown reaches zero."}
          </p>
        </div>
        {#if !sent}
          <div class="actions">
            <button
              type="button"
              class="secondary"
              disabled={busy}
              on:click={() => changeSchedule(paused ? "resume" : "pause")}
            >{paused ? "Resume timer" : "Pause timer"}</button>
            <button
              type="button"
              disabled={busy}
              on:click={() => (sendConfirmationOpen = true)}
            >Send out now</button>
          </div>
        {/if}
      </div>

      {#if sent}
        <div class="delivery-success">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Invitations sent successfully</strong>
            <p>{lastInvitationSendCount
              ? `${lastInvitationSendCount} invitation${lastInvitationSendCount === 1 ? "" : "s"} were sent immediately.`
              : "All selected participants have received their invitation."}</p>
          </div>
        </div>
      {:else}
        <div class="countdown-panel">
          <span>{paused ? "Time remaining" : "Automatic send in"}</span>
          <strong>{formatCountdown(remaining)}</strong>
          <small>{pending} pending invitation{pending === 1 ? "" : "s"}</small>
        </div>
      {/if}

      {#if !sent}
        <div class="delivery-note">
          <strong>Reliable automatic delivery</strong>
          <p>The schedule continues on the server even when this page is closed. A paused timer resumes from the same remaining time.</p>
        </div>
      {/if}

      {#if sent}
        <section class="reminder-section">
          <div class="reminder-header">
            <div>
              <h2>Reminders</h2>
              <p>Automatic and manually sent reminders share the same limit of three per participant.</p>
            </div>
            <div class="reminder-actions">
              <span>{reminderRoundsSent} of {reminderLimit} sent</span>
              <button
                type="button"
                disabled={busy || !canSendReminder}
                on:click={sendReminderNow}
              >Send reminder now</button>
            </div>
          </div>

          {#if reminderSchedules.length}
            <div class="reminder-grid">
              {#each reminderSchedules as reminder}
                <article>
                  <span>{reminder.label}</span>
                  <strong>{formatReminderCountdown(reminder.sendAt)}</strong>
                  <small>{formatReminderDate(reminder.sendAt)}</small>
                </article>
              {/each}
            </div>
          {:else}
            <p class="empty-reminders">No automatic reminders are configured. You can still send reminders manually.</p>
          {/if}
          {#if finished}<p class="finished-note">This survey is finished; no further reminders can be sent.</p>{/if}
        </section>
      {/if}

      {#if errorMessage}<p class="error">{errorMessage}</p>{/if}
      {#if feedbackMessage}<p class="feedback">{feedbackMessage}</p>{/if}
    </section>
  {/if}
</main>

<Modal
  bind:open={sendConfirmationOpen}
  modalHeading="Send invitations now?"
  actions
  width={440}
  primaryButtonText={busy ? "Sending..." : "Send out now"}
  secondaryButtonText="Cancel"
  primaryButtonValidations={busy}
  primaryButtonAction={sendNow}
  secondaryButtonAction={() => {
    sendConfirmationOpen = false;
    errorMessage = "";
  }}
>
  <div class="confirmation-content">
    <strong>{pending} pending invitation{pending === 1 ? "" : "s"} will be sent immediately.</strong>
    <p>The automatic invitation countdown will be removed after successful delivery.</p>
    {#if errorMessage}<p class="error">{errorMessage}</p>{/if}
  </div>
</Modal>

<style>
  .invitation-page {
    min-height: calc(100vh - 3.5rem);
    padding: 1rem;
    background: var(--pax-light-gray, #f2f2f2);
  }

  .invitation-card {
    padding: 1.5rem;
    border-radius: 0.5rem;
    background: var(--pax-white, #fff);
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #d9d9d9;
  }

  h1 { margin: 0.65rem 0 0.35rem; font-size: 1.35rem; }
  p { margin: 0; color: #4d5358; }

  .status-badge {
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    color: #744d00;
    background: #fff0bd;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
  }
  .status-badge.paused { color: #525252; background: #e8e8e8; }
  .status-badge.sent { color: #146c2e; background: #c8f4ce; }

  .actions { display: flex; gap: 0.5rem; }
  button {
    min-height: 2.5rem;
    padding: 0 1rem;
    border: 1px solid var(--pax-black, #1e1e22);
    border-radius: 0.5rem;
    color: white;
    background: var(--pax-black, #1e1e22);
    cursor: pointer;
  }
  button.secondary { color: var(--pax-black, #1e1e22); background: white; }
  button:disabled { cursor: wait; opacity: 0.55; }

  .countdown-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 1.5rem 0;
    padding: 3rem 1rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: #fafafa;
  }
  .countdown-panel span { color: #525252; }
  .countdown-panel strong {
    margin: 0.5rem 0;
    font-size: clamp(2.5rem, 7vw, 5rem);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.04em;
  }
  .countdown-panel small { color: #6f6f6f; }

  .delivery-success {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 1.5rem 0;
    padding: 1.5rem;
    border: 1px solid #8ed69a;
    border-radius: 0.5rem;
    background: #effbf1;
  }
  .delivery-success > span {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    color: white;
    background: #238636;
    font-weight: 700;
  }
  .delivery-success p { margin-top: 0.25rem; }

  .delivery-note { padding: 1rem; border-left: 3px solid var(--pax-orange, #ff5a1f); background: #fafafa; }
  .delivery-note p { margin-top: 0.35rem; }
  .error { margin-top: 1rem; color: #c62828; }
  .feedback { margin-top: 1rem; color: #146c2e; }
  .confirmation-content p { margin-top: 0.5rem; }

  .reminder-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #d9d9d9;
  }
  .reminder-header,
  .reminder-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .reminder-header h2 { margin: 0 0 0.35rem; font-size: 1.1rem; }
  .reminder-actions span { white-space: nowrap; color: #525252; font-size: 0.8rem; }
  .reminder-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }
  .reminder-grid article {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: #fafafa;
  }
  .reminder-grid strong { margin: 0.45rem 0; font-size: 1.5rem; font-variant-numeric: tabular-nums; }
  .reminder-grid small,
  .reminder-grid span,
  .empty-reminders,
  .finished-note { color: #6f6f6f; }
  .empty-reminders,
  .finished-note { margin-top: 1rem; }

  @media (max-width: 48rem) {
    .card-header { flex-direction: column; }
    .actions, button { width: 100%; }
    .reminder-header,
    .reminder-actions { align-items: stretch; flex-direction: column; }
    .reminder-grid { grid-template-columns: 1fr; }
  }
</style>
